import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { useState } from 'react';
import { checkEventEditAccess, getEventById } from '@/server/event';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Navbar } from '@/components/ui/navbar';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  ArrowLeft,
  Building2,
  Users,
  Pencil,
  Menu,
  X,
  Target,
} from 'lucide-react';

export const Route = createFileRoute('/admin/dashboard/event/$eventId')({
  component: EventDashboardLayout,
  beforeLoad: async ({ context, location, params }) => {
    if (!context.profile) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }

    const { hasAccess } = await checkEventEditAccess({
      data: params.eventId,
    });
    if (!hasAccess) {
      throw redirect({
        to: '/event/$eventId',
        params: { eventId: params.eventId },
        search: { unauthorized: true },
      });
    }

    return context.profile;
  },
  loader: async ({ params }) => {
    const event = await getEventById({ data: params.eventId });
    return { event };
  },
  errorComponent: ({ error }) => {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="p-6 text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Dashboard Not Found
            </h1>
            <p className="text-gray-600 mb-4">
              {error?.message ||
                "The event you're looking for doesn't exist."}
            </p>
            <Link to="/search">
              <Button>Browse Events</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  },
});

const navItemClass =
  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors';

/** Sidebar navigation links shared between desktop and mobile drawer. */
function SidebarNav({
  isInternalEvent,
  eventId,
  organizerId,
  onNavigate,
}: {
  isInternalEvent: boolean;
  eventId: string;
  organizerId: string | null;
  onNavigate?: () => void;
}) {
  const items = [
    {
      to: '/admin/dashboard/event/$eventId' as const,
      label: 'Overview',
      icon: Target,
      internalOnly: false,
      activeOptions: { exact: true },
    },
    {
      to: '/admin/dashboard/event/$eventId/participants' as const,
      label: 'Participants',
      icon: Users,
      internalOnly: true,
      activeOptions: { exact: false },
    },
    {
      to: '/admin/dashboard/event/$eventId/edit' as const,
      label: 'Edit Event',
      icon: Pencil,
      internalOnly: false,
      activeOptions: { exact: false },
    },
  ];

  return (
    <nav className="space-y-1">
      {items.map((item) => {
        const Icon = item.icon;
        const isEnabled = item.internalOnly ? isInternalEvent : true;
        if (!isEnabled) return null;

        return (
          <Link
            key={item.label}
            to={item.to}
            params={{ eventId }}
            activeOptions={item.activeOptions}
            onClick={onNavigate}
            className={`${navItemClass} text-gray-700 hover:bg-gray-100`}
            activeProps={{
              className: `${navItemClass} bg-blue-50 text-blue-700`,
            }}
          >
            <Icon className="w-4 h-4" />
            {item.label}
          </Link>
        );
      })}
      <div className="border-t border-gray-200 my-3" />
      {organizerId && (
        <Link
          to="/admin/dashboard/organizer/$organizerId"
          params={{ organizerId }}
          onClick={onNavigate}
          className={`${navItemClass} text-gray-700 hover:bg-gray-100`}
        >
          <Building2 className="w-4 h-4" />
          Organizer Dashboard
        </Link>
      )}
      <Link
        to="/event/$eventId"
        params={{ eventId }}
        onClick={onNavigate}
        className={`${navItemClass} text-gray-700 hover:bg-gray-100`}
      >
        <ArrowLeft className="w-4 h-4" />
        View Event
      </Link>
    </nav>
  );
}

/** Dashboard layout with sidebar navigation and routed content via Outlet. */
function EventDashboardLayout() {
  const { event } = Route.useLoaderData();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          {/* Mobile menu trigger */}
          <Drawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            direction="left"
          >
            <DrawerTrigger asChild>
              <button
                type="button"
                className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100"
              >
                <Menu className="w-5 h-5 text-gray-700" />
              </button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader className="flex items-center justify-between">
                <DrawerTitle>Dashboard</DrawerTitle>
                <DrawerClose asChild>
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-gray-100"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </DrawerClose>
              </DrawerHeader>
              <div className="px-4 pb-4">
                <SidebarNav
                  eventId={event.id}
                  organizerId={event.organizerId}
                  isInternalEvent={!event.externalId}
                  onNavigate={() => setDrawerOpen(false)}
                />
              </div>
            </DrawerContent>
          </Drawer>

          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 truncate">
              {event.name}
            </h1>
            <p className="text-xs text-gray-500">Event Dashboard</p>
          </div>
        </div>
      </div>

      {/* Main layout */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Desktop sidebar */}
          <div className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-20">
              <SidebarNav
                eventId={event.id}
                organizerId={event.organizerId}
                isInternalEvent={!event.externalId}
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
